



























import { Vue, Component, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import user from '@/modules/auth/auth.store'
import venue from '@/modules/venue/venue.store'
import shared from '@/modules/shared/shared.store'
import { ConfigAPI, ResponseStatus } from './api/config'
import ServerError from '@/utils/server_error'
import { isElectron } from './utils/electron'
import { getGameTypes } from '@/utils/gameType'
import InstantMatchSnackBar from '@/modules/instant/components/InstantMatchSnackBar'
import TournamentEntryFeePaymentDialog from '@/modules/payment/components/TournamentEntryFeePaymentDialog'

@Component({
  components: { TournamentEntryFeePaymentDialog, InstantMatchSnackBar }
})
export default class App extends Vue {
  userModule = getModule(user, this.$store)
  venueModule = getModule(venue, this.$store)
  sharedModule = getModule(shared, this.$store)

  authTimeout: boolean = false

  async created() {
    if (isElectron()) {
      if (!(await this.userModule.updateLoginState())) {
        this.$toast.error('Your session has expired. Please relogin.')
      }
      document.body.classList.add('electron')
    } else {
      // Prompt sign-up dialog if auto-login
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.get('auto-login') === '1') {
        await this.userModule.signIn()
        return
      }

      // Restoring user session if available.
      try {
        this.userModule.authenticateOidcSilent().then()
      } catch {}

      setTimeout(() => {
        this.authTimeout = true
        console.log('Auth timed out.')
      }, 500)
    }

    this.getApplicationConfigs().then()
    this.sharedModule.updateSuggestedRegion().then()
  }

  get accessToken() {
    return this.userModule.oidcAccessToken
  }

  get authState() {
    return (
      this.accessToken ||
      this.userModule.oidcAuthenticationIsChecked ||
      this.authTimeout
    )
  }

  @Watch('accessToken')
  async onAccessTokenChanged(accessToken: string) {
    await this.userModule.setAuthToken(accessToken)
    await this.userModule.updateLoginState()
    await this.sharedModule.updateSuggestedRegion()
  }

  @Watch('authState')
  async onAuthStateChanged() {
    await this.userModule.updateLoginState()
  }

  async getApplicationConfigs() {
    try {
      const response = await ConfigAPI.getSettings()
      let payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        // Avatars
        const memberAvatars = payload.avatars
        this.sharedModule.setApplicationSupportedAvatars(memberAvatars)

        // Games
        const supportedGames = payload.games
        const gameTypes = getGameTypes()
        this.sharedModule.setApplicationSupportedGames(supportedGames)

        // Regions
        this.sharedModule.setApplicationRegions(payload.regions)

        // Venue
        if (!payload.applicationVenue) {
          if (process.env.NODE_ENV !== 'production') {
            console.log(
              '[DEBUG] Localhost is skipped from IP validation and the IP address does not belong to any venues. Please check venue IP configuration if you want to support application venue e.g. set home shop'
            )
          }
          return
        }
        const venue = payload.applicationVenue
        this.venueModule.setApplicationVenue(venue)
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      }
      console.error(err)
    }
  }

  get analyticsUrl() {
    let routeName = this.$route.name

    if (!routeName) {
      routeName = this.$route.fullPath
    }

    return `${process.env.VUE_APP_ANALYTICS_URL}?name=${encodeURIComponent(
      routeName.toString()
    )}&params=${JSON.stringify(this.$route.params)}`
  }

  get isElectron() {
    return isElectron()
  }

  get shouldRender() {
    return this.sharedModule.shouldRender
  }

  get isEmbedded() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('embedded') === '1'
  }
}
